import React, { useEffect, useState } from "react";
import { Grid, Typography } from "@mui/material";
import { FormattedMessage } from "react-intl";
import { useNavigate } from "react-router-dom";

import Header from "@/components/Header";
import GetImages from "@/components/GetImages";
import DropDownSelect from "@/components/DropDownSelect";
import Confirmation from "@/components/Confirmation";
import FullScreenDialog from "@/components/FullScreenDialog";
import HelpSupport from "@/views/Doctor/HelpSupport";

import style from "./index.module.css";

import {
  clearLocalStorageExcept,
  languagesListInfo,
  topBarDropdownDispatchingEnums,
  transformClasses,
} from "@/utils";
import { storage } from "@/utils/Storage";
import useLogout from "@/utils/hooks/useLogout";
import { usePermissionRestriction } from "@/utils/hooks/usePermissionRestriction";

import { useAppDispatch, useAppSelector } from "@/store/hooks";
import {
  fetchConfiguration,
  setIsSettingPreferencesOpenClose,
} from "@/store/slices/config";
import { setTopBarDropdownItems } from "@/store/slices/doctor/topNavigation";
import { setSteps } from "@/store/slices/onboarding/onboarding";

import { IDropDownSelectionType, IProfileDropdownEnums } from "@/models/doctor";
import { ILanguageEnums, IOnboardingSteps } from "@/models/onboarding";
import { IFilterType } from "@/models/patient";
import { IRoleType } from "@/models/role";
import { IModalKeyEnums, IUserPermissionEnums } from "@/models/permission";
import { ISessionKeys } from "@/models";

const SettingPreferences: React.FC = () => {
  const [isLanguageSelectOpen, setIsLanguageSelectOpen] =
    useState<boolean>(false);
  const [selectedLang, setSelectedLang] = useState<string>("");
  const [isShowLogoutWarningModal, setIsShowLogoutWarningModal] =
    useState<boolean>(false);

  const roleType = storage.getSessionToken(ISessionKeys.ROLE_TYPE);

  const navigate = useNavigate();
  const { logout } = useLogout();
  const permissionRestrictionHandler = usePermissionRestriction();
  const dispatch = useAppDispatch();
  const configDetail: any = useAppSelector((state) => state.config);
  const topBarItems = useAppSelector(
    (state) => state.topNavigation.topBarItems
  );

  const handleDialog = () => {
    setIsShowLogoutWarningModal((state) => !state);
  };

  const openCloseLanguageSelectionModal = () => {
    setIsLanguageSelectOpen(!isLanguageSelectOpen);
  };

  const saveFilteredLanguagesValues = (
    value: string,
    type: IDropDownSelectionType
  ) => {
    openCloseLanguageSelectionModal();
    setSelectedLang(value);
    storage.setSessionToken(ISessionKeys.LANG, value);
    window.location.reload();
    return;
  };

  const openCloseHelpSupportModalHandler = () => {
    dispatch(
      setTopBarDropdownItems(
        topBarDropdownDispatchingEnums[IProfileDropdownEnums.HELP_SUPPORT]
      )
    );
  };

  const logoutUserHandler = () => {
    if (roleType === IRoleType.GUEST) {
      dispatch(setIsSettingPreferencesOpenClose());
      clearLocalStorageExcept(ISessionKeys.LOCATION);
      dispatch(setSteps(IOnboardingSteps.VERIFY_MOBILE_NUMBER));
      navigate("/login");
      return;
    }
    const isLogoutPermissionGranted = permissionRestrictionHandler(
      IModalKeyEnums.USER,
      IUserPermissionEnums.LOGOUT
    );
    if (!isLogoutPermissionGranted) return;
    handleDialog();
  };

  useEffect(() => {
    if (Object.keys(configDetail?.config).length === 0) {
      dispatch(fetchConfiguration(IFilterType.FIND_DOCTOR));
    }
  }, []);

  useEffect(() => {
    setSelectedLang(storage.getSessionToken("lang") ?? ILanguageEnums.ENG);
  }, [configDetail]);

  return (
    <FullScreenDialog>
      <Grid item>
        <Header
          heading="Settings & Preferences"
          previousPage={() => dispatch(setIsSettingPreferencesOpenClose())}
        />
        {(roleType === IRoleType.PATIENT || roleType === IRoleType.GUEST) && (
          <Grid
            item
            className={transformClasses(style.boxContainer, style.spacing)}
          >
            <Typography component={"p"} className={style.languageHeading}>
              <FormattedMessage defaultMessage={"Language"} id="language" />
            </Typography>
            <Grid item className={style.selectedLanguageContainer}>
              <Typography component={"p"} className={style.selectedLanguage}>
                {languagesListInfo[selectedLang]}
              </Typography>
              <Typography
                component={"p"}
                className={style.languageChangeHandle}
                onClick={openCloseLanguageSelectionModal}
              >
                <FormattedMessage defaultMessage={"Change"} id="change" />
              </Typography>
            </Grid>
          </Grid>
        )}
        {(roleType === IRoleType.DOCTOR ||
          roleType === IRoleType.JR_DOCTOR) && (
          <Grid
            item
            className={transformClasses(
              style.boxContainer,
              style.logoutAction,
              style.spacingTop
            )}
            onClick={openCloseHelpSupportModalHandler}
          >
            <GetImages name="HelpSupportIcon" width="28" height="20" />
            <Typography component={"p"}>
              <FormattedMessage
                defaultMessage={"Help & Support"}
                id="help_support"
              />
            </Typography>
          </Grid>
        )}
        <Grid
          item
          className={transformClasses(
            style.boxContainer,
            style.logoutAction,
            roleType !== IRoleType.PATIENT ? style.spacingTop : ""
          )}
          onClick={logoutUserHandler}
        >
          <GetImages
            name={roleType === IRoleType.GUEST ? "LoginIcon" : "LogoutIcon"}
            width="28"
            height="20"
          />
          <Typography component={"p"}>
            <FormattedMessage
              id={roleType === IRoleType.GUEST ? "loginAndRegister" : "logout"}
              defaultMessage={
                roleType === IRoleType.GUEST ? "Login / Register" : "Log Out"
              }
            />
          </Typography>
        </Grid>

        {isLanguageSelectOpen && (
          <DropDownSelect
            btnLabel="confirm"
            type={IDropDownSelectionType.LANGUAGES}
            isOpen={isLanguageSelectOpen}
            closeHandler={openCloseLanguageSelectionModal}
            title="Select Language"
            values={configDetail?.config?.findDoctor?.language.map(
              (name: ILanguageEnums, index: number) => ({
                label: languagesListInfo[name],
                value: name,
              })
            )}
            selectedValue={selectedLang}
            saveSelectedValues={saveFilteredLanguagesValues}
          />
        )}
      </Grid>
      <Confirmation
        isOpen={isShowLogoutWarningModal}
        closeHandler={handleDialog}
        title="logout"
        alterMessage={"logout_alert_message"}
        actionHandler={logout}
      />
      {topBarItems?.isHelpSupportOpen && (
        <HelpSupport
          isOpen={topBarItems?.isHelpSupportOpen}
          closeHandler={openCloseHelpSupportModalHandler}
        />
      )}
    </FullScreenDialog>
  );
};

export default SettingPreferences;
